import './styles.scss';

import { ChangeEvent, FocusEvent, MouseEvent, ReactElement, Ref, forwardRef, useState } from 'react';
import { cleanDatetimeRegExp, formatDatetime } from '@/common/DatetimeInput/utils';

import { DATETIME_FORMAT } from '@/constants';
import { DateInputProps } from '@/components/DateInput';
import DatetimeInputPopover from '@/common/DatetimeInput/DatetimeInputPopover';
import { DatetimeInputTypes } from '@/common/DatetimeInput/types';
import PopoverInput from '@/common/PopoverInput';
import { TimeInputTypes } from '@/common/TimeInput/types';
import { getClasses } from '@/utils';
import useForwardRef from '@/hooks/useForwardRef';

export type DatetimeInputProps = DateInputProps & TimeInputTypes.Props;

const DatetimeInput = forwardRef<DatetimeInputTypes.RefType, DatetimeInputTypes.Props>(
  (
    {
      format = DATETIME_FORMAT,
      showHours = true,
      showMinutes = true,
      showSeconds = false,
      disableHours = false,
      disableMinutes = false,
      disableSeconds = false,
      ...props
    }: DatetimeInputTypes.Props,
    ref: Ref<DatetimeInputTypes.RefType>
  ): ReactElement => {
    // Input Ref/State
    const inputRef = useForwardRef(ref);
    const [value, setValue] = useState<string>(props.value || '');

    // Handle Change
    const handleChange = (
      newValue: string,
      focus: boolean = false,
      event?: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>
    ): string => {
      setValue(newValue);
      props.onChange?.(newValue, (event || { target: inputRef.current }) as ChangeEvent<HTMLInputElement>);
      if (focus) inputRef.current?.focus();
      return newValue;
    };

    // Patch: useImperativeHandle to spread input element properties
    // (for ref compatibility with PopoverInputTypes.RefType)
    // This is only needed if DatetimeInput ever uses useImperativeHandle directly.
    // If not, PopoverInput's patch is sufficient.
    return (
      <PopoverInput
        placeholder={format}
        onFormat={formatDatetime(format)}
        {...props}
        type="format"
        value={value}
        className={getClasses('DatetimeInput', 'Common', props.className)}
        onChange={(value: string, event: ChangeEvent<HTMLInputElement>): void => {
          value = value.replace(cleanDatetimeRegExp, '');
          event.target.value = handleChange(value, false, event);
        }}
        onBlur={(value: string, event: FocusEvent<HTMLInputElement>): void => {
          handleChange(value, false, event);
          props.onBlur?.(value, event);
        }}
        ref={inputRef}
      >
        <DatetimeInputPopover
          value={value}
          format={format}
          target={inputRef}
          onChange={handleChange}
          showHours={showHours}
          showMinutes={showMinutes}
          showSeconds={showSeconds}
          disableHours={disableHours}
          disableMinutes={disableMinutes}
          disableSeconds={disableSeconds}
        />
      </PopoverInput>
    );
  }
);
DatetimeInput.displayName = 'DatetimeInput';

export default DatetimeInput;
